/**
 * Configuración de la vista de lista "Contenido" (medios/imágenes).
 * ListView + opción de vista lista/cuadrícula en la propia página.
 */

import { ContentIcon } from '@/lib/polaris-icons';
import type { ListViewConfig } from '@/types/listView.types';
import { ROUTES } from '@/config/routes.config';

export interface ContentListItem {
  id: string;
  name: string;
  url: string;
  bucket: string;
  size_display: string;
  created_at_display: string;
  /** Nombre corto para tabla (archivo sin ruta) */
  displayName: string;
}

export type NavigateFn = (path: string) => void;

export interface ContentListConfigOptions {
  onBack: () => void;
  onUpload: () => void;
}

/**
 * Config de lista de contenido (imágenes/medios).
 * La página puede mostrar esta lista en modo tabla (ListView) o cuadrícula (Grid).
 */
export function getContentListConfig(
  options: ContentListConfigOptions
): ListViewConfig<ContentListItem> {
  const { onBack, onUpload } = options;
  return {
    title: 'Contenido',
    titleIcon: ContentIcon,
    onBack,
    backAccessibilityLabel: 'Volver al inicio',
    primaryAction: {
      content: 'Subir imagen',
      onAction: onUpload,
    },
    resourceName: { singular: 'imagen', plural: 'imágenes' },
    columns: [
      {
        key: 'url',
        title: 'Imagen',
        type: 'thumbnail',
        imageKey: 'url',
        altKey: 'displayName',
      },
      {
        key: 'displayName',
        title: 'Nombre',
        type: 'text',
        fontWeight: 'medium',
      },
      {
        key: 'size_display',
        title: 'Tamaño',
        type: 'text',
        tone: 'subdued',
      },
      {
        key: 'created_at_display',
        title: 'Fecha',
        type: 'text',
        tone: 'subdued',
      },
      {
        key: 'bucket',
        title: 'Bucket',
        type: 'text',
        tone: 'subdued',
      },
      {
        key: 'actions',
        title: 'Acciones',
        type: 'actions',
        deleteLabel: 'Eliminar',
        delete: true,
      },
    ],
    emptyState: {
      heading: 'No hay imágenes subidas',
      description: 'Sube imágenes para usarlas en tus productos y contenido.',
      image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png',
      action: {
        content: 'Subir imagen',
        onAction: onUpload,
      },
    },
    bulkActions: [
      { content: 'Exportar datos', actionKey: 'export' },
      // "Eliminar" lo inyecta la plantilla vía prop `bulkDelete`.
    ],
    idKey: 'id',
  };
}
